import type { OperatorDeviceCommandType } from '@ghostscreen/contracts';
import { i18n } from '../../../i18n';
import type { CardConnectionStatus } from '../../../lib/dashboard/screenStatus';
import { formatRelativeTime } from '../../../lib/dashboard/relativeTime';

export type FixCommandKind = 'restart' | 'sync' | 'cache';

export type FixCommandDef = {
  kind: FixCommandKind;
  type: OperatorDeviceCommandType;
};

export const FIX_COMMANDS: readonly FixCommandDef[] = [
  { kind: 'restart', type: 'RESTART_PLAYER' },
  { kind: 'sync', type: 'SYNC_NOW' },
  { kind: 'cache', type: 'CLEAR_CACHE' },
] as const;

export type FixRunPhase = 'running' | 'queued';

export type FixVisibility = {
  /** Show the Fix this screen block. */
  showSection: boolean;
  /** Show “Looks wrong on the TV?” next to status. */
  showLink: boolean;
  /** Show Hide under the section. */
  canHide: boolean;
  intro: string;
};

/**
 * Visibility + copy for Fix this screen (Ghost Screen Displays.dc.html).
 * Online + idle → collapsed with link. Offline/unknown → open. Busy → stays open.
 */
export function buildFixVisibility(input: {
  connection: CardConnectionStatus;
  busy: boolean;
  anyQueued: boolean;
  manuallyOpen: boolean;
}): FixVisibility {
  const { connection, busy, anyQueued, manuallyOpen } = input;
  const live = connection === 'online';
  const needsAttention = connection === 'offline' || connection === 'unknown';
  const showSection = needsAttention || busy || manuallyOpen;

  let intro = i18n.t('displays:fix.intro.default');
  if (!live) {
    intro = anyQueued
      ? i18n.t('displays:fix.intro.offlineQueued')
      : i18n.t('displays:fix.intro.offline');
    if (connection === 'unknown') {
      intro = anyQueued
        ? i18n.t('displays:fix.intro.unknownQueued')
        : i18n.t('displays:fix.intro.unknown');
    }
  }

  return {
    showSection,
    showLink: !showSection,
    canHide: live && !busy,
    intro,
  };
}

export function buildStatusLine(
  connection: CardConnectionStatus,
  lastHeartbeatAt: string | null,
  now = Date.now(),
): string {
  const seen = formatRelativeTime(lastHeartbeatAt, now).toLowerCase();
  switch (connection) {
    case 'online':
      return i18n.t('displays:fix.statusLine.online', { seen });
    case 'offline':
      return i18n.t('displays:fix.statusLine.offline', { seen });
    case 'unknown':
      return i18n.t('displays:fix.statusLine.unknown', { seen });
    default:
      return i18n.t('displays:fix.statusLine.notPaired');
  }
}

export function statusLabel(connection: CardConnectionStatus): string {
  switch (connection) {
    case 'online':
      return i18n.t('displays:fix.status.online');
    case 'offline':
      return i18n.t('displays:fix.status.offline');
    case 'unknown':
      return i18n.t('displays:fix.status.unknown');
    default:
      return i18n.t('displays:fix.status.notSetUp');
  }
}

export function statusColorVar(connection: CardConnectionStatus): string {
  switch (connection) {
    case 'online':
      return 'var(--gs-ok)';
    case 'offline':
      return 'var(--gs-bad)';
    case 'unknown':
      return 'var(--gs-warn)';
    default:
      return 'var(--gs-muted)';
  }
}

export function fixButtonLabel(def: FixCommandDef, live: boolean): string {
  return live
    ? i18n.t(`displays:fix.commands.${def.kind}.button`)
    : i18n.t(`displays:fix.commands.${def.kind}.buttonWhenBack`);
}

export function phaseForConnection(connection: CardConnectionStatus): FixRunPhase {
  return connection === 'online' ? 'running' : 'queued';
}
